import { fetchWithAuth } from '../../services/api';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_BACKEND_URL || '';

/** Parse response as JSON; if server returned HTML (wrong URL / 404), throw a clear error. */
async function parseJsonResponse(response, context) {
  const text = await response.text();
  const trimmed = text.trim();
  if (trimmed.startsWith('<') || trimmed.startsWith('<!')) {
    throw new Error(
      `Server returned HTML instead of JSON. Check that REACT_APP_API_BASE_URL (or REACT_APP_BACKEND_URL) points to your API (current: "${API_BASE_URL || '(empty)'}") and the backend is running.`
    );
  }
  try {
    return JSON.parse(text);
  } catch (e) {
    throw new Error(`${context}: invalid JSON from server. ${e.message}`);
  }
}

/** Build an Error carrying the server-provided message (e.g. "delete blocked while checkpoints exist"). */
async function extractApiError(response, fallback) {
  try {
    const data = await response.json();
    const message = data?.message || data?.msg || data?.error;
    if (message) return new Error(message);
  } catch (e) {
    // body was not JSON – fall through to generic message
  }
  return new Error(`${fallback} (${response.status})`);
}

class SettingsService {
  async getSettings() {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/settings`, { method: 'GET' });

      if (!response.ok) {
        throw new Error('Failed to fetch settings');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching settings:', error);
      throw error;
    }
  }

  async updateSettings(settingsData) {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/settings`, {
        method: 'PUT',
        body: JSON.stringify(settingsData),
      });

      if (!response.ok) {
        throw new Error('Failed to update settings');
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating settings:', error);
      throw error;
    }
  }

  async getInspectionFields(section, type) {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/settings/inspection-fields?section=${section}&type=${type}`, { method: 'GET' });

      if (!response.ok) {
        throw new Error('Failed to fetch inspection fields');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching inspection fields:', error);
      throw error;
    }
  }

  async updateInspectionFields(section, type, fields) {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/settings/inspection-fields`, {
        method: 'PUT',
        body: JSON.stringify({
          section,
          type,
          fields,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update inspection fields');
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating inspection fields:', error);
      throw error;
    }
  }

  /** Get all machines – equipmentCategoryId 10001 for Machine */
  async getMachines(equipmentCategoryId = 10001) {
    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/api/v1/machine/get-all-machines?equipmentCategoryId=${equipmentCategoryId}`,
        { method: 'GET' }
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch machines (${response.status})`);
      }
      return await parseJsonResponse(response, 'Machines API');
    } catch (error) {
      console.error('Error fetching machines:', error);
      throw error;
    }
  }

  /** Get all tools & tackles – equipmentCategoryId 10002 for Tools & Tackles */
  async getToolsTackles(equipmentCategoryId = 10002) {
    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/api/v1/tools-tackles/get-all-tools-tackles?equipmentCategoryId=${equipmentCategoryId}`,
        { method: 'GET' }
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch tools and tackles (${response.status})`);
      }
      return await parseJsonResponse(response, 'Tools & Tackles API');
    } catch (error) {
      console.error('Error fetching tools and tackles:', error);
      throw error;
    }
  }


  // ---------- Machine Identification & Description Fields ----------
  /** GetAll: GET /api/v1/identification-description-field/get-all-machine-identification-descriptions-field?machineId= */
  async getAllMachineIdentificationDescriptions(machineId) {
    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/api/v1/identification-description-field/get-all-machine-identification-descriptions-field?machineId=${machineId}`,
        { method: 'GET' }
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch identification descriptions (${response.status})`);
      }
      return await parseJsonResponse(response, 'Get all machine identification descriptions');
    } catch (error) {
      console.error('Error fetching machine identification descriptions:', error);
      throw error;
    }
  }

  /** Get one: GET /api/v1/identification-description-field/get-machine-identification-description-field?identificationDescriptionId= */
  async getMachineIdentificationDescription(identificationDescriptionId) {
    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/api/v1/identification-description-field/get-machine-identification-description-field?identificationDescriptionId=${identificationDescriptionId}`,
        { method: 'GET' }
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch identification description (${response.status})`);
      }
      return await parseJsonResponse(response, 'Get machine identification description');
    } catch (error) {
      console.error('Error fetching machine identification description:', error);
      throw error;
    }
  }

  /** Add: POST /api/v1/identification-description-field/add-machine-identification-description-field */
  async addMachineIdentificationDescription(body) {
    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/api/v1/identification-description-field/add-machine-identification-description-field`,
        { method: 'POST', body: JSON.stringify(body) }
      );
      if (!response.ok) {
        throw new Error(`Failed to add identification description (${response.status})`);
      }
      return await parseJsonResponse(response, 'Add machine identification description');
    } catch (error) {
      console.error('Error adding machine identification description:', error);
      throw error;
    }
  }

  /** Edit: PUT /api/v1/identification-description-field/edit-machine-identification-description-field */
  async editMachineIdentificationDescription(body) {
    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/api/v1/identification-description-field/edit-machine-identification-description-field`,
        { method: 'PUT', body: JSON.stringify(body) }
      );
      if (!response.ok) {
        throw new Error(`Failed to update identification description (${response.status})`);
      }
      return await parseJsonResponse(response, 'Edit machine identification description');
    } catch (error) {
      console.error('Error updating machine identification description:', error);
      throw error;
    }
  }

  /** Delete: DELETE /api/v1/identification-description-field/delete-machine-identification-description-field?identificationDescriptionId= */
  async deleteMachineIdentificationDescription(identificationDescriptionId) {
    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/api/v1/identification-description-field/delete-machine-identification-description-field?identificationDescriptionId=${identificationDescriptionId}`,
        { method: 'DELETE' }
      );
      if (!response.ok) {
        throw new Error(`Failed to delete identification description (${response.status})`);
      }
      return await parseJsonResponse(response, 'Delete machine identification description');
    } catch (error) {
      console.error('Error deleting machine identification description:', error);
      throw error;
    }
  }

  // ---------- Tools & Tackles Identification & Description Fields ----------
  /** GetAll: GET get-all-tools-tackles-identification-descriptions-field?toolsTacklesId= */
  async getAllToolsTacklesIdentificationDescriptions(toolsTacklesId) {
    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/api/v1/identification-description-field/get-all-tools-tackles-identification-descriptions-field?toolsTacklesId=${toolsTacklesId}`,
        { method: 'GET' }
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch tools & tackles identification descriptions (${response.status})`);
      }
      return await parseJsonResponse(response, 'Get all tools & tackles identification descriptions');
    } catch (error) {
      console.error('Error fetching tools & tackles identification descriptions:', error);
      throw error;
    }
  }

  /** Get one: GET get-tools-tackles-identification-description-field?identificationDescriptionId= */
  async getToolsTacklesIdentificationDescription(identificationDescriptionId) {
    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/api/v1/identification-description-field/get-tools-tackles-identification-description-field?identificationDescriptionId=${identificationDescriptionId}`,
        { method: 'GET' }
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch tools & tackles identification description (${response.status})`);
      }
      return await parseJsonResponse(response, 'Get tools & tackles identification description');
    } catch (error) {
      console.error('Error fetching tools & tackles identification description:', error);
      throw error;
    }
  }

  /** Add: POST add-tools-tackles-identification-description-field */
  async addToolsTacklesIdentificationDescription(body) {
    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/api/v1/identification-description-field/add-tools-tackles-identification-description-field`,
        { method: 'POST', body: JSON.stringify(body) }
      );
      if (!response.ok) {
        throw new Error(`Failed to add tools & tackles identification description (${response.status})`);
      }
      return await parseJsonResponse(response, 'Add tools & tackles identification description');
    } catch (error) {
      console.error('Error adding tools & tackles identification description:', error);
      throw error;
    }
  }

  /** Edit: PUT edit-tools-tackles-identification-description-field */
  async editToolsTacklesIdentificationDescription(body) {
    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/api/v1/identification-description-field/edit-tools-tackles-identification-description-field`,
        { method: 'PUT', body: JSON.stringify(body) }
      );
      if (!response.ok) {
        throw new Error(`Failed to update tools & tackles identification description (${response.status})`);
      }
      return await parseJsonResponse(response, 'Edit tools & tackles identification description');
    } catch (error) {
      console.error('Error updating tools & tackles identification description:', error);
      throw error;
    }
  }

  /** Delete: DELETE delete-tools-tackles-identification-description-field?identificationDescriptionId= */
  async deleteToolsTacklesIdentificationDescription(identificationDescriptionId) {
    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/api/v1/identification-description-field/delete-tools-tackles-identification-description-field?identificationDescriptionId=${identificationDescriptionId}`,
        { method: 'DELETE' }
      );
      if (!response.ok) {
        throw new Error(`Failed to delete tools & tackles identification description (${response.status})`);
      }
      return await parseJsonResponse(response, 'Delete tools & tackles identification description');
    } catch (error) {
      console.error('Error deleting tools & tackles identification description:', error);
      throw error;
    }
  }

  // ---------- Machine Inspection Checkpoints ----------
  /** GetAll: GET get-all-machine-inspection-checkpoints-field?machineId=
   * New API response format: { totalCount, machineId, mainPoints: [{ mainPointId, mainPointName, inspectionCheckpoints: [...] }] }
   * Returns raw response with nested structure
   */
  async getAllMachineInspectionCheckpoints(machineId) {
    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/api/v1/inspection-checkpoint-field/get-all-machine-inspection-checkpoints-field?machineId=${machineId}`,
        { method: 'GET' }
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch machine inspection checkpoints (${response.status})`);
      }
      return await parseJsonResponse(response, 'Get all machine inspection checkpoints');
    } catch (error) {
      console.error('Error fetching machine inspection checkpoints:', error);
      throw error;
    }
  }

  /** Helper: Extract flat checkpoint list from nested response structure
   * Flattens the mainPoints[].inspectionCheckpoints[] array into a single list
   * Adds mainPointId and mainPointName to each checkpoint for proper association
   */
  flattenCheckpointsFromNestedResponse(data) {
    if (!data || !data.mainPoints) return [];
    const flattened = [];
    data.mainPoints.forEach((mainPoint) => {
      if (mainPoint.inspectionCheckpoints && Array.isArray(mainPoint.inspectionCheckpoints)) {
        mainPoint.inspectionCheckpoints.forEach((checkpoint) => {
          flattened.push({
            ...checkpoint,
            mainPointId: mainPoint.mainPointId,
            mainPointName: mainPoint.mainPointName,
          });
        });
      }
    });
    return flattened;
  }

  /** Get one: GET get-machine-inspection-checkpoint-field?inspectionCheckpointId= */
  async getMachineInspectionCheckpoint(inspectionCheckpointId) {
    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/api/v1/inspection-checkpoint-field/get-machine-inspection-checkpoint-field?inspectionCheckpointId=${inspectionCheckpointId}`,
        { method: 'GET' }
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch machine inspection checkpoint (${response.status})`);
      }
      return await parseJsonResponse(response, 'Get machine inspection checkpoint');
    } catch (error) {
      console.error('Error fetching machine inspection checkpoint:', error);
      throw error;
    }
  }

  /** Add: POST add-machine-inspection-checkpoint-field */
  async addMachineInspectionCheckpoint(body) {
    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/api/v1/inspection-checkpoint-field/add-machine-inspection-checkpoint-field`,
        { method: 'POST', body: JSON.stringify(body) }
      );
      if (!response.ok) {
        throw await extractApiError(response, 'Failed to add machine inspection checkpoint');
      }
      return await parseJsonResponse(response, 'Add machine inspection checkpoint');
    } catch (error) {
      console.error('Error adding machine inspection checkpoint:', error);
      throw error;
    }
  }

  /** Edit: PUT edit-machine-inspection-checkpoint-field */
  async editMachineInspectionCheckpoint(body) {
    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/api/v1/inspection-checkpoint-field/edit-machine-inspection-checkpoint-field`,
        { method: 'PUT', body: JSON.stringify(body) }
      );
      if (!response.ok) {
        throw await extractApiError(response, 'Failed to update machine inspection checkpoint');
      }
      return await parseJsonResponse(response, 'Edit machine inspection checkpoint');
    } catch (error) {
      console.error('Error updating machine inspection checkpoint:', error);
      throw error;
    }
  }

  /** Delete: DELETE delete-machine-inspection-checkpoint-field?inspectionCheckpointId= */
  async deleteMachineInspectionCheckpoint(inspectionCheckpointId) {
    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/api/v1/inspection-checkpoint-field/delete-machine-inspection-checkpoint-field?inspectionCheckpointId=${inspectionCheckpointId}`,
        { method: 'DELETE' }
      );
      if (!response.ok) {
        throw await extractApiError(response, 'Failed to delete machine inspection checkpoint');
      }
      return await parseJsonResponse(response, 'Delete machine inspection checkpoint');
    } catch (error) {
      console.error('Error deleting machine inspection checkpoint:', error);
      throw error;
    }
  }

  // ---------- Machine Inspection Checkpoint Main Points ----------
  /** GetAll: GET get-all-machine-inspection-checkpoint-main-points?machineId=
   * Response: { machineId, totalCount, mainPoints: [{ mainPointId, mainPointName, machineId, displayOrder, checkpointCount }] }
   */
  async getAllMachineMainPoints(machineId) {
    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/api/v1/inspection-checkpoint-field/get-all-machine-inspection-checkpoint-main-points?machineId=${machineId}`,
        { method: 'GET' }
      );
      if (!response.ok) {
        throw await extractApiError(response, 'Failed to fetch main points');
      }
      return await parseJsonResponse(response, 'Get all machine main points');
    } catch (error) {
      console.error('Error fetching machine main points:', error);
      throw error;
    }
  }

  /** Add: POST add-machine-inspection-checkpoint-main-point
   * Body: { machineId, mainPointName, displayOrder }
   */
  async addMachineMainPoint(body) {
    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/api/v1/inspection-checkpoint-field/add-machine-inspection-checkpoint-main-point`,
        { method: 'POST', body: JSON.stringify(body) }
      );
      if (!response.ok) {
        throw await extractApiError(response, 'Failed to add main point');
      }
      return await parseJsonResponse(response, 'Add machine main point');
    } catch (error) {
      console.error('Error adding machine main point:', error);
      throw error;
    }
  }

  /** Edit: PUT edit-machine-inspection-checkpoint-main-point
   * Body: { mainPointId, mainPointName, displayOrder }
   */
  async editMachineMainPoint(body) {
    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/api/v1/inspection-checkpoint-field/edit-machine-inspection-checkpoint-main-point`,
        { method: 'PUT', body: JSON.stringify(body) }
      );
      if (!response.ok) {
        throw await extractApiError(response, 'Failed to update main point');
      }
      return await parseJsonResponse(response, 'Edit machine main point');
    } catch (error) {
      console.error('Error updating machine main point:', error);
      throw error;
    }
  }

  /** Delete (soft): DELETE delete-machine-inspection-checkpoint-main-point?mainPointId=
   * Blocked by the server while active checkpoints still exist under the main point.
   */
  async deleteMachineMainPoint(mainPointId) {
    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/api/v1/inspection-checkpoint-field/delete-machine-inspection-checkpoint-main-point?mainPointId=${mainPointId}`,
        { method: 'DELETE' }
      );
      if (!response.ok) {
        throw await extractApiError(response, 'Failed to delete main point');
      }
      return await parseJsonResponse(response, 'Delete machine main point');
    } catch (error) {
      console.error('Error deleting machine main point:', error);
      throw error;
    }
  }

  // ---------- Tools & Tackles Inspection Checkpoint Main Points ----------
  /** GetAll: GET get-all-tools-tackles-inspection-checkpoint-main-points?toolsTacklesId=
   * Response: { toolsTacklesId, totalCount, mainPoints: [{ mainPointId, mainPointName, toolsTacklesId, displayOrder, checkpointCount }] }
   */
  async getAllToolsTacklesMainPoints(toolsTacklesId) {
    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/api/v1/inspection-checkpoint-field/get-all-tools-tackles-inspection-checkpoint-main-points?toolsTacklesId=${toolsTacklesId}`,
        { method: 'GET' }
      );
      if (!response.ok) {
        throw await extractApiError(response, 'Failed to fetch main points');
      }
      return await parseJsonResponse(response, 'Get all tools & tackles main points');
    } catch (error) {
      console.error('Error fetching tools & tackles main points:', error);
      throw error;
    }
  }

  /** Add: POST add-tools-tackles-inspection-checkpoint-main-point
   * Body: { toolsTacklesId, mainPointName, displayOrder }
   */
  async addToolsTacklesMainPoint(body) {
    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/api/v1/inspection-checkpoint-field/add-tools-tackles-inspection-checkpoint-main-point`,
        { method: 'POST', body: JSON.stringify(body) }
      );
      if (!response.ok) {
        throw await extractApiError(response, 'Failed to add main point');
      }
      return await parseJsonResponse(response, 'Add tools & tackles main point');
    } catch (error) {
      console.error('Error adding tools & tackles main point:', error);
      throw error;
    }
  }

  /** Edit: PUT edit-tools-tackles-inspection-checkpoint-main-point
   * Body: { mainPointId, mainPointName, displayOrder }
   */
  async editToolsTacklesMainPoint(body) {
    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/api/v1/inspection-checkpoint-field/edit-tools-tackles-inspection-checkpoint-main-point`,
        { method: 'PUT', body: JSON.stringify(body) }
      );
      if (!response.ok) {
        throw await extractApiError(response, 'Failed to update main point');
      }
      return await parseJsonResponse(response, 'Edit tools & tackles main point');
    } catch (error) {
      console.error('Error updating tools & tackles main point:', error);
      throw error;
    }
  }

  /** Delete (soft): DELETE delete-tools-tackles-inspection-checkpoint-main-point?mainPointId=
   * Blocked by the server while active checkpoints still exist under the main point.
   */
  async deleteToolsTacklesMainPoint(mainPointId) {
    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/api/v1/inspection-checkpoint-field/delete-tools-tackles-inspection-checkpoint-main-point?mainPointId=${mainPointId}`,
        { method: 'DELETE' }
      );
      if (!response.ok) {
        throw await extractApiError(response, 'Failed to delete main point');
      }
      return await parseJsonResponse(response, 'Delete tools & tackles main point');
    } catch (error) {
      console.error('Error deleting tools & tackles main point:', error);
      throw error;
    }
  }

  // ---------- Tools & Tackles Inspection Checkpoints ----------
  /** GetAll: GET get-all-tools-tackles-inspection-checkpoints-field?toolsTacklesId=
   * New API response format: { totalCount, toolsTacklesId, mainPoints: [{ mainPointId, mainPointName, inspectionCheckpoints: [...] }] }
   * Returns raw response with nested structure
   */
  async getAllToolsTacklesInspectionCheckpoints(toolsTacklesId) {
    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/api/v1/inspection-checkpoint-field/get-all-tools-tackles-inspection-checkpoints-field?toolsTacklesId=${toolsTacklesId}`,
        { method: 'GET' }
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch tools & tackles inspection checkpoints (${response.status})`);
      }
      return await parseJsonResponse(response, 'Get all tools & tackles inspection checkpoints');
    } catch (error) {
      console.error('Error fetching tools & tackles inspection checkpoints:', error);
      throw error;
    }
  }

  /** Get one: GET get-tools-tackles-inspection-checkpoint-field?inspectionCheckpointId= */
  async getToolsTacklesInspectionCheckpoint(inspectionCheckpointId) {
    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/api/v1/inspection-checkpoint-field/get-tools-tackles-inspection-checkpoint-field?inspectionCheckpointId=${inspectionCheckpointId}`,
        { method: 'GET' }
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch tools & tackles inspection checkpoint (${response.status})`);
      }
      return await parseJsonResponse(response, 'Get tools & tackles inspection checkpoint');
    } catch (error) {
      console.error('Error fetching tools & tackles inspection checkpoint:', error);
      throw error;
    }
  }

  /** Add: POST add-tools-tackles-inspection-checkpoint-field */
  async addToolsTacklesInspectionCheckpoint(body) {
    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/api/v1/inspection-checkpoint-field/add-tools-tackles-inspection-checkpoint-field`,
        { method: 'POST', body: JSON.stringify(body) }
      );
      if (!response.ok) {
        throw await extractApiError(response, 'Failed to add tools & tackles inspection checkpoint');
      }
      return await parseJsonResponse(response, 'Add tools & tackles inspection checkpoint');
    } catch (error) {
      console.error('Error adding tools & tackles inspection checkpoint:', error);
      throw error;
    }
  }

  /** Edit: PUT edit-tools-tackles-inspection-checkpoint-field */
  async editToolsTacklesInspectionCheckpoint(body) {
    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/api/v1/inspection-checkpoint-field/edit-tools-tackles-inspection-checkpoint-field`,
        { method: 'PUT', body: JSON.stringify(body) }
      );
      if (!response.ok) {
        throw await extractApiError(response, 'Failed to update tools & tackles inspection checkpoint');
      }
      return await parseJsonResponse(response, 'Edit tools & tackles inspection checkpoint');
    } catch (error) {
      console.error('Error updating tools & tackles inspection checkpoint:', error);
      throw error;
    }
  }

  /** Delete: DELETE delete-tools-tackles-inspection-checkpoint-field?inspectionCheckpointId= */
  async deleteToolsTacklesInspectionCheckpoint(inspectionCheckpointId) {
    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/api/v1/inspection-checkpoint-field/delete-tools-tackles-inspection-checkpoint-field?inspectionCheckpointId=${inspectionCheckpointId}`,
        { method: 'DELETE' }
      );
      if (!response.ok) {
        throw await extractApiError(response, 'Failed to delete tools & tackles inspection checkpoint');
      }
      return await parseJsonResponse(response, 'Delete tools & tackles inspection checkpoint');
    } catch (error) {
      console.error('Error deleting tools & tackles inspection checkpoint:', error);
      throw error;
    }
  }
}

export default new SettingsService();

